import { useState } from 'react';
import { Building2, FileText, Briefcase, DollarSign, Clock, Cpu, StickyNote, Pencil, Check } from 'lucide-react';
import { Button } from '@solid/ui';

interface ExtractedMetadata {
  companyName?: string;
  projectDescription?: string;
  projectType?: string;
  budget?: string;
  timeline?: string;
  techRequirements?: string;
  additionalNotes?: string;
}

interface ExtractionReviewProps {
  metadata: ExtractedMetadata;
  documents?: Array<{ fileName: string; documentType: string }>;
  onContinue: (correctedMetadata: ExtractedMetadata) => void;
  onBack?: () => void;
}

const fields: Array<{
  key: keyof ExtractedMetadata;
  label: string;
  icon: React.ElementType;
  multiline?: boolean;
}> = [
  { key: 'companyName', label: 'Company', icon: Building2 },
  { key: 'projectDescription', label: 'Project Description', icon: FileText, multiline: true },
  { key: 'projectType', label: 'Project Type', icon: Briefcase },
  { key: 'budget', label: 'Budget', icon: DollarSign },
  { key: 'timeline', label: 'Timeline', icon: Clock },
  { key: 'techRequirements', label: 'Tech Requirements', icon: Cpu, multiline: true },
  { key: 'additionalNotes', label: 'Additional Notes', icon: StickyNote, multiline: true },
];

export function ExtractionReview({
  metadata,
  documents = [],
  onContinue,
  onBack,
}: ExtractionReviewProps) {
  const [editedMetadata, setEditedMetadata] = useState<ExtractedMetadata>({ ...metadata });
  const [editingField, setEditingField] = useState<keyof ExtractedMetadata | null>(null);

  const populatedFields = fields.filter((f) => editedMetadata[f.key]?.trim());
  const emptyFields = fields.filter((f) => !editedMetadata[f.key]?.trim());

  const handleFieldChange = (key: keyof ExtractedMetadata, value: string) => {
    setEditedMetadata((prev) => ({ ...prev, [key]: value }));
  };

  const handleFieldBlur = () => {
    setEditingField(null);
  };

  const handleContinue = () => {
    onContinue(editedMetadata);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-500/20">
            <FileText className="h-4 w-4 text-green-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-white">
              {documents.length === 1
                ? 'Document analyzed successfully'
                : `${documents.length} documents analyzed`}
            </p>
            {documents.length === 1 && (
              <p className="text-xs text-slate-400 truncate">{documents[0].fileName}</p>
            )}
          </div>
        </div>
        {documents.length > 1 && (
          <div className="mt-2 pl-11 space-y-1">
            {documents.map((doc, i) => (
              <p key={i} className="text-xs text-slate-400 truncate">
                {doc.fileName}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Extracted fields */}
      {populatedFields.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Extracted from your document
            </p>
            <p className="text-xs text-slate-500">
              Click any field to edit
            </p>
          </div>
          <div className="space-y-2">
            {populatedFields.map((field) => {
              const Icon = field.icon;
              const value = editedMetadata[field.key] || '';
              const isEditing = editingField === field.key;
              return (
                <div
                  key={field.key}
                  className={`rounded-lg border p-4 transition-colors ${
                    isEditing
                      ? 'border-accent-500/50 bg-slate-800'
                      : 'border-slate-700 bg-slate-800 hover:border-slate-600 cursor-pointer'
                  }`}
                  onClick={() => {
                    if (!isEditing) setEditingField(field.key);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Icon className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-slate-400 mb-1">
                        {field.label}
                      </p>
                      {isEditing ? (
                        field.multiline ? (
                          <textarea
                            className="w-full bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-accent-500 resize-y min-h-[60px]"
                            value={value}
                            onChange={(e) => handleFieldChange(field.key, e.target.value)}
                            onBlur={handleFieldBlur}
                            autoFocus
                            rows={3}
                          />
                        ) : (
                          <input
                            type="text"
                            className="w-full bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-accent-500"
                            value={value}
                            onChange={(e) => handleFieldChange(field.key, e.target.value)}
                            onBlur={handleFieldBlur}
                            autoFocus
                          />
                        )
                      ) : (
                        <div className="flex items-start gap-2">
                          <p className="text-sm text-white leading-relaxed flex-1">
                            {value}
                          </p>
                          <Pencil className="h-3 w-3 shrink-0 mt-1 text-slate-500" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-700 bg-slate-800 p-6 text-center">
          <p className="text-sm text-slate-400">
            We couldn't extract specific project details from your document.
            No worries — you can add details below, or we'll ask a few follow-up questions next.
          </p>
        </div>
      )}

      {/* Empty fields — allow user to fill in anything AI missed */}
      {emptyFields.length > 0 && populatedFields.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
            Anything we missed? (optional)
          </p>
          <div className="space-y-2">
            {emptyFields.map((field) => {
              const Icon = field.icon;
              const value = editedMetadata[field.key] || '';
              const isEditing = editingField === field.key;
              return (
                <div
                  key={field.key}
                  className={`rounded-lg border p-4 transition-colors ${
                    isEditing
                      ? 'border-accent-500/50 bg-slate-800'
                      : 'border-slate-700/50 bg-slate-800/50 hover:border-slate-600 cursor-pointer'
                  }`}
                  onClick={() => {
                    if (!isEditing) setEditingField(field.key);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Icon className="h-4 w-4 mt-0.5 shrink-0 text-slate-500" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-slate-500 mb-1">
                        {field.label}
                      </p>
                      {isEditing ? (
                        field.multiline ? (
                          <textarea
                            className="w-full bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-accent-500 resize-y min-h-[60px]"
                            value={value}
                            onChange={(e) => handleFieldChange(field.key, e.target.value)}
                            onBlur={handleFieldBlur}
                            autoFocus
                            placeholder={`Add ${field.label.toLowerCase()}...`}
                            rows={3}
                          />
                        ) : (
                          <input
                            type="text"
                            className="w-full bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-accent-500"
                            value={value}
                            onChange={(e) => handleFieldChange(field.key, e.target.value)}
                            onBlur={handleFieldBlur}
                            autoFocus
                            placeholder={`Add ${field.label.toLowerCase()}...`}
                          />
                        )
                      ) : (
                        <p className="text-sm text-slate-600 italic">
                          Click to add...
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-700"
          >
            Back
          </button>
        )}
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="flex-1 bg-accent-600 hover:bg-accent-700 text-white"
          onClick={handleContinue}
        >
          <Check className="h-4 w-4 mr-2" />
          Looks good — continue
        </Button>
      </div>
    </div>
  );
}
